import React from 'react';
import { Check, Store, MapPin, Tag } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export interface DatoAutocompletado {
  /** Texto visible, p. ej. "Razón social". */
  label: string;
  /** Ícono de lucide-react que representa el dato. */
  icon: LucideIcon;
}

export interface AvisoAutocompletadoProps {
  /** Datos que la consulta traerá desde SUNAT. */
  items?: DatoAutocompletado[];
  /** Texto del encabezado. */
  titulo?: string;
  /** Nota al pie. Pasa `null` para ocultarla. */
  nota?: string | null;
  className?: string;
}

const ITEMS_DEFAULT: DatoAutocompletado[] = [
  { label: 'Razón social', icon: Store },
  { label: 'Domicilio fiscal', icon: MapPin },
  { label: 'Actividad económica', icon: Tag },
];

const TITULO_DEFAULT = 'Al consultar, completaremos automáticamente';
const NOTA_DEFAULT =
  'Tú eliges el régimen tributario. Si tu RUC no tiene nombre comercial registrado, también podrás escribirlo.';

/**
 * Bloque informativo del estado inicial. Va dentro de la tarjeta "Datos de tu empresa",
 * debajo del campo RUC, y se oculta en cuanto llega la respuesta de SUNAT.
 *
 * El check verde sobre cada ícono es estático: significa "este dato viene incluido
 * en la consulta", no "ya está completado".
 *
 * @example
 * ```tsx
 * {!rucValidado && <AvisoAutocompletado />}
 * ```
 */
export const AvisoAutocompletado: React.FC<AvisoAutocompletadoProps> = ({
  items = ITEMS_DEFAULT,
  titulo = TITULO_DEFAULT,
  nota = NOTA_DEFAULT,
  className = '',
}) => (
  <div className={`mt-5 pt-5 border-t border-muted ${className}`}>
    <p className="text-caption-medium text-tertiary mb-3.5">{titulo}</p>

    {/* pb-1 da aire a la insignia, que sobresale de la tesela y si no se recorta */}
    <ul className="flex flex-wrap gap-x-7 gap-y-4 pb-1">
      {items.map(({ label, icon: Icon }) => (
        <li key={label} className="flex items-center gap-2.5">
          <span className="relative w-9 h-9 rounded-lg border border-muted bg-surface-1 text-subtle flex items-center justify-center shrink-0">
            <Icon width={18} height={18} strokeWidth={1.8} />
            <span
              className="absolute -right-1 -bottom-1 w-[17px] h-[17px] rounded-full bg-success text-inverse border-2 border-[var(--surface-0)] flex items-center justify-center"
              aria-hidden="true"
            >
              <Check width={9} height={9} strokeWidth={4} className="shrink-0" />
            </span>
          </span>
          <span className="text-body-sm text-secondary">{label}</span>
        </li>
      ))}
    </ul>

    {nota && <p className="text-caption text-tertiary mt-4">{nota}</p>}
  </div>
);
